import React, {useEffect, useRef, useState} from 'react'
import {
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form'
import {IApplicationFormData} from '../../models/application-form'
import {Modal} from 'react-bootstrap'
import {deleteCoApplicantInvite} from '../../api'

interface ApplicationTypeStepProps {
  register: UseFormRegister<IApplicationFormData>
  errors?: FieldErrors<IApplicationFormData>
  setValue: UseFormSetValue<IApplicationFormData>
  watch: UseFormWatch<IApplicationFormData>
  trigger: UseFormTrigger<IApplicationFormData>
  triggerValidation?: () => Promise<boolean>
}

const options: Array<{
  value: NonNullable<IApplicationFormData['applicationType']>
  title: string
  subtitle: string
  switchTitle?: string
  switchDangerText?: string
  switchSuccessText?: string
  icon: React.ReactElement
}> = [
  {
    value: 'Individual',
    title: 'Just me',
    subtitle: 'Just you on the lease.',
    switchTitle: 'Switch to Just me?',
    switchDangerText:
      'A solo application doesnt include co-applicants - you can start a new shared card later if things chage.',
    switchSuccessText:
      'Your information stays. Income, ID, documents, preferences - all of it carries over to your new Just me application.',

    icon: (
      <svg width='22' height='22' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
        <circle
          cx='10'
          cy='7'
          r='3'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M4 17c.6-3.2 3.2-5 6-5s5.4 1.8 6 5'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
  },
  {
    value: 'Couple',
    title: 'Me and my partner',
    subtitle: 'Both of you on the lease - both incomes count.',
    switchTitle: 'Switch to Me and my partner?',
    switchDangerText:
      'Co-applicants and occupants from your previous selection wont carry over. You can add new ones on the next screens.',
    switchSuccessText:
      'Your information stays. Income, ID, documents, preferences - all of it carries over to your new Me and my partner application.',
    icon: (
      <svg width='22' height='22' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
        <circle
          cx='7'
          cy='7'
          r='2.5'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <circle
          cx='14'
          cy='8'
          r='2'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M2.5 16c.5-2.5 2.4-4 4.5-4s4 1.5 4.5 4M12 16c.5-2 1.8-3 3-3s2.3 1 2.5 3'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
  },
  {
    value: 'Housemates',
    title: 'Housemates',
    subtitle: 'Everyone on the lease, splitting rent.',
    switchTitle: 'Switch to Housemates?',
    switchDangerText:
      'Co-applicants and occupants from your previous selection wont carry over. You can add new ones on the next screens.',
    switchSuccessText:
      'Your information stays. Income, ID, documents, preferences - all of it carries over to your new Housemates application.',

    icon: (
      <svg width='22' height='22' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
        <circle
          cx='6'
          cy='7'
          r='2.2'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <circle
          cx='14'
          cy='7'
          r='2.2'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <circle
          cx='10'
          cy='12'
          r='2.2'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M2 14.5c.4-1.8 1.8-2.8 4-2.8M18 14.5c-.4-1.8-1.8-2.8-4-2.8M5 17.5c.4-1.8 2.2-3 5-3s4.6 1.2 5 3'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
  },
  {
    value: 'Family',
    title: 'Family',
    subtitle: 'One or two adults on the lease, plus dependants.',
    switchTitle: 'Switch to Family?',
    switchDangerText:
      'Family applications can include a partner and dependants - you can add them on the next screens.',
    switchSuccessText:
      'Your information stays. Income, ID, documents, preferences - all of it carries over to your new Family application.',

    icon: (
      <svg width='22' height='22' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
        <circle
          cx='6'
          cy='5.5'
          r='1.8'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <circle
          cx='14'
          cy='5.5'
          r='1.8'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <circle
          cx='10'
          cy='13'
          r='1.5'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M2 11.5c.4-2 1.7-3 4-3M18 11.5c-.4-2-1.7-3-4-3M6 17.5c.5-2 1.9-3 4-3s3.5 1 4 3'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
  },
  /*{
    value: 'CompanyTrust',
    title: 'Company / Trust',
    subtitle: 'Business entity',
    icon: 'bi-building',
  },*/
]

const ApplicationTypeStep: React.FC<ApplicationTypeStepProps> = ({
  register,
  errors,
  setValue,
  watch,
  trigger,
  triggerValidation,
}) => {
  const selectedType = watch('applicationType')
  const sessionId = watch('sessionId')
  const coApplicants = watch('coApplicants') ?? []
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmChange, setConfirmChange] = useState(false)
  const [isApplyingTypeChange, setIsApplyingTypeChange] = useState(false)
  const [typeChangeError, setTypeChangeError] = useState<string | null>(null)
  const initialTriggerValidation = useRef(triggerValidation)

  const [pendingType, setPendingType] = useState<NonNullable<
    IApplicationFormData['applicationType']
  > | null>(null)

  const deleteCoApplicants = async () => {
    const normalizedSessionId = sessionId?.trim()
    const inviteIds = Array.from(
      new Set(
        coApplicants
          .map((applicant) => applicant.inviteId?.trim())
          .filter((inviteId): inviteId is string => Boolean(inviteId))
      )
    )

    if (normalizedSessionId && inviteIds.length > 0) {
      // Active-member removals now wait for their replacement workflow. Keep
      // departures sequential so they cannot prepare conflicting compositions.
      for (const inviteId of inviteIds) {
        const result = await deleteCoApplicantInvite({
          sessionId: normalizedSessionId,
          inviteId,
        })
        if (!result.success) throw new Error(result.message || 'Unable to delete every co-applicant.')
      }
    }

    setValue('coApplicants', [], {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  const applyTypeChange = async (value: NonNullable<IApplicationFormData['applicationType']>) => {
    await deleteCoApplicants()

    setValue('applicationType', value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })

    await trigger?.(['applicationType', 'coApplicants'])
  }

  const selectType = (value: NonNullable<IApplicationFormData['applicationType']>) => {
    if (!selectedType) {
      void applyTypeChange(value)
      return
    }

    if (selectedType === value) {
      return
    }

    setPendingType(value)
    setTypeChangeError(null)
    setShowConfirmModal(true)
  }

  const handleConfirm = () => {
    setConfirmChange(true)
  }

  const handleCancel = () => {
    if (isApplyingTypeChange) {
      return
    }

    setPendingType(null)
    setTypeChangeError(null)
    setShowConfirmModal(false)
  }

  const handleChange = async () => {
    if (!pendingType || isApplyingTypeChange) {
      return
    }

    setIsApplyingTypeChange(true)
    setTypeChangeError(null)

    try {
      await applyTypeChange(pendingType)
      setPendingType(null)
      setConfirmChange(false)
      setShowConfirmModal(false)
    } catch {
      setTypeChangeError('Unable to delete every co-applicant. Please try again.')
    } finally {
      setIsApplyingTypeChange(false)
    }
  }

  const handleGoBack = () => {
    if (isApplyingTypeChange) {
      return
    }

    setConfirmChange(false)
  }

  useEffect(() => {
    void initialTriggerValidation.current?.()
  }, [])

  const individualOption = options.find((option) => option.value === pendingType)

  return (
    <>
      <div>
        <input type='hidden' {...register('applicationType')} />

        <div className='text-center mt-10 mb-7'>
          <h2 className='wizard-auth-title'>Who&apos;s moving in?</h2>
        </div>

        <div className='d-grid gap-3 m-3'>
          {options.map((option) => {
            const isActive = selectedType === option.value

            return (
              <button
                key={option.value}
                type='button'
                className={`wizard-selection-card btn text-start ${isActive ? 'active' : ''}`}
                onClick={() => selectType(option.value)}
                aria-pressed={isActive}
              >
                <div className='d-flex align-items-center justify-content-between gap-3'>
                  <div className='d-flex align-items-center gap-3'>
                    <div className='wizard-selection-icon'>{option.icon}</div>
                    <div>
                      <div className='fw-semibold'>{option.title}</div>
                      <div className='small-text'>{option.subtitle}</div>
                    </div>
                  </div>
                </div>
              </button>
            )
          })}
        </div>

        {errors?.applicationType && (
          <div className='text-danger mt-3'>{String(errors.applicationType.message)}</div>
        )}
      </div>

      <Modal
        show={showConfirmModal}
        onHide={handleCancel}
        backdrop={isApplyingTypeChange ? 'static' : true}
        keyboard={!isApplyingTypeChange}
        centered
        dialogClassName='switch-type-modal'
      >
        <Modal.Body className='switch-type-modal__body'>
          {!confirmChange && (
            <div id='switchTo'>
              <div className='switch-type-modal__icon'>
                <svg
                  width='22'
                  height='22'
                  viewBox='0 0 20 20'
                  fill='none'
                  stroke='#DC2626'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M4 5.5h12M8 5.5V4a1.5 1.5 0 011.5-1.5h1A1.5 1.5 0 0112 4v1.5M5.5 5.5l.8 10A1.5 1.5 0 007.8 17h4.4a1.5 1.5 0 001.5-1.5l.8-10'></path>
                </svg>
              </div>

              <h4 className='switch-type-modal__title'>{individualOption?.switchTitle}</h4>

              <div className='switch-type-modal__message danger'>
                <span>✕</span>
                <p>{individualOption?.switchDangerText}</p>
              </div>

              <div className='switch-type-modal__message success'>
                <span>✓</span>
                <p>{individualOption?.switchSuccessText}</p>
              </div>

              <div className='switch-type-modal__actions'>
                <button type='button' className='btn btn-outline-secondary' onClick={handleCancel}>
                  Cancel
                </button>

                <button type='button' className='btn btn-danger' onClick={handleConfirm}>
                  Yes, switch to {individualOption?.title}
                </button>
              </div>
            </div>
          )}
          {confirmChange && (
            <div id='confirm'>
              <div className='switch-type-modal__icon'>
                <svg
                  width='22'
                  height='22'
                  viewBox='0 0 20 20'
                  fill='none'
                  stroke='#DC2626'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M10 2.5l8 14H2l8-14z'></path>
                  <path d='M10 8v3.5M10 14v0'></path>
                </svg>
              </div>

              <h4 className='switch-type-modal__title'>Are you sure?</h4>

              <div className='switch-type-modal__message mt-0'>
                <p>
                  This is your last chance to go back. We'll switch your application to{' '}
                  <strong>{individualOption?.title}</strong>. This can't be undone.
                </p>
              </div>

              {typeChangeError && (
                <div className='text-danger small mb-3' role='alert'>
                  {typeChangeError}
                </div>
              )}

              <div className='switch-type-modal__actions'>
                <button
                  type='button'
                  className='btn btn-outline-secondary'
                  onClick={handleGoBack}
                  disabled={isApplyingTypeChange}
                >
                  Go Back
                </button>

                <button
                  type='button'
                  className='btn btn-danger'
                  onClick={handleChange}
                  disabled={isApplyingTypeChange}
                >
                  {isApplyingTypeChange ? 'Deleting co-applicants...' : 'Confirm Switch'}
                </button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}

export default ApplicationTypeStep
